import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Smile, 
  Calendar, 
  Clock, 
  Users, 
  Heart,
  CheckCircle2,
  Sun,
  Moon,
  Sunrise,
  Building2,
  Phone,
  Mail
} from 'lucide-react';

/**
 * Staff Dashboard
 * Simple welcome page for staff members
 */
const StaffDashboard = () => {
  const { user } = useAuth();

  // Get current time and greeting
  const getCurrentGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour < 12) {
      return { greeting: 'Good Morning', icon: Sunrise, color: 'bg-orange-100 text-orange-600' };
    } else if (hour < 17) {
      return { greeting: 'Good Afternoon', icon: Sun, color: 'bg-yellow-100 text-yellow-600' };
    } else if (hour < 21) {
      return { greeting: 'Good Evening', icon: Moon, color: 'bg-indigo-100 text-indigo-600' };
    } else {
      return { greeting: 'Good Night', icon: Moon, color: 'bg-purple-100 text-purple-600' };
    }
  };

  const { greeting, icon: GreetingIcon, color } = getCurrentGreeting();

  // Get current date
  const getCurrentDate = () => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date().toLocaleDateString('en-US', options);
  };

  // Get current time
  const [currentTime, setCurrentTime] = React.useState(
    new Date().toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  );

  // Update time every minute
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(
        new Date().toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })
      );
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Staff Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">{greeting}, {user?.name?.split(' ')[0] || 'Staff'}!</p>
      </div>


      {/* Date and Time Card */}
      <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-lg ${color} dark:bg-opacity-20`}>
            <GreetingIcon className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{greeting}!</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Welcome back to your dashboard</p>
          </div>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500">Today's Date</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{getCurrentDate()}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500">Current Time</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{currentTime}</p>
            </div>
          </div>
        </div>
      </div>


      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Your Role</p>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Staff Member</p>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-500">User ID</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.user_id || 'N/A'}</p>
          </div>
        </div>


        {/* Tasks Card */}
        <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Your Tasks</p>
            <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Coming Soon</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">Task management will be available soon</p>
        </div>


        {/* Care Quality Card */}
        <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Care Quality</p>
            <div className="bg-pink-100 dark:bg-pink-900/30 p-2 rounded-lg">
              <Heart className="w-5 h-5 text-pink-600 dark:text-pink-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Excellence</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">Providing compassionate care</p>
        </div>
      </div>


      {/* Welcome Message */}
      <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
        <div className="flex items-start gap-4">
          <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg flex-shrink-0">
            <Smile className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">You're Making a Difference!</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              Thank you for your dedication and compassionate care. Your work brings 
              comfort, joy, and quality of life to those who need it most. Every day, 
              you make a positive impact in the lives of our residents and their families.
            </p>
          </div>
        </div>
      </div>


      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Quick Tips */}
        <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Quick Tips</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
              <span>Use the menu to navigate between different sections</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
              <span>Check your assigned tasks regularly</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
              <span>Update resident information as needed</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
              <span>Report any issues to your manager</span>
            </li>
          </ul>
        </div>


        {/* Need Help */}
        <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border border-transparent dark:border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
              <Heart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Need Help?</h3>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            If you need assistance or have any questions, don't hesitate to reach out:
          </p>
          <ul className="space-y-3">
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <CheckCircle2 className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <span>Contact your supervisor</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <CheckCircle2 className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <span>Check the help documentation</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <CheckCircle2 className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <span>Attend team meetings</span>
            </li>
          </ul>
        </div>
      </div>


      {/* Motivational Quote */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-lg shadow p-6 border-l-4 border-blue-500">
        <p className="text-lg italic text-gray-700 dark:text-gray-300 mb-2 text-center">
          "Caring for others is the highest expression of human excellence."
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium text-center">
          Keep up the amazing work! 💙
        </p>
      </div>


      {/* Footer Message */}
      <div className="text-center">
        <p className="text-sm text-gray-500">
          Remember: Every small act of kindness makes a big difference
        </p>
      </div>
    </div>
  );
};

export default StaffDashboard;
